import queryString from 'qs';
import { setDestIfNeeded } from '@utils/intendedDest';
import isArray from 'lodash/isArray';

const BASE_URL = '/api/v1/';

const headers = { 'Content-Type': 'application/json' };

const host = window.location.protocol + '//' + window.location.host;
const hostMinusPort =
  window.location.protocol + '//' + window.location.hostname;

function normalizeURL(url) {
  if (url.startsWith(host)) {
    // make absolute urls relative if needed
    url = url.substr(host.length);
  } else if (url.startsWith(hostMinusPort)) {
    // Sometimes, we get "next_page" URLs that don't include the port. That
    // seems to be a flaw in our PHP pagination layer. Just do the same thing
    // if port isn't given.
    url = url.substr(hostMinusPort.length);
  }

  if (url.startsWith(BASE_URL)) {
    return url;
  } else if (url.startsWith('/')) {
    return BASE_URL + url.slice(1);
  } else {
    return BASE_URL + url;
  }
}

export function getCookieValue(key) {
  const results = document.cookie.match('(^|;)\\s*' + key + '\\s*=\\s*([^;]+)');
  return results ? results.pop() : '';
}

function getHeaders(method) {
  let headersForRequest = { ...headers };
  if (method !== 'GET') {
    // attempt to use staging cookie first. (this cookie is set only for the staging server to avoid multi domain match
    // since the prod cookie is for the root domain and staging is for subdomain)
    let cookie = getCookieValue('STAGING-XSRF-TOKEN');
    if (!cookie || cookie.length === 0) {
      cookie = getCookieValue('XSRF-TOKEN');
    }
    headersForRequest['X-XSRF-TOKEN'] = decodeURIComponent(cookie);
  }
  return headersForRequest;
}

function _asyncRequest(
  url,
  method,
  body = null,
  normalize = true,
  authRequest = false,
  isJson = true
) {
  const headersForRequest = getHeaders(method);
  if (body) {
    body = JSON.stringify(body);
  }
  const result = {
    success: false,
    status: null,
    data: null,
  };
  url = normalize ? normalizeURL(url) : url;
  return fetch(url, {
    body,
    credentials: 'include',
    headers: headersForRequest,
    method,
  })
    .then((response) => {
      result.status = response.status;
      result.success = response.status < 400;

      // 204 means "No Content", so there shouldn't be anything in the body.
      if (response.status !== 204 && isJson) {
        return response.json();
      }
      return response.text();
    })
    .then((data) => {
      if (
        result.status === 401 &&
        !authRequest &&
        !window.location.pathname.startsWith('/login') &&
        !window.location.pathname.startsWith('/signups') &&
        !window.location.pathname.startsWith('/dash/ratings')
      ) {
        // don't redirect if we are already on the login page
        // not using history.push here so that we completely reset redux/in-memory state
        setDestIfNeeded();
        let redirect = '/login';
        const qs = queryString.parse(window.location.search.substring(1));
        const ref = qs.ref;
        const provider = qs.provider;
        if (ref) {
          redirect += `?ref=${ref}`;
        }
        if (provider) {
          redirect += (ref ? '&' : '?') + `provider=${provider}`;
        }
        window.location = redirect;
      }
      result.data = data;
      return result;
    })
    .catch((error) => {
      result.success = false;
      return result;
    });
}

export function get(url, normalize = true, isJson = true) {
  return _asyncRequest(url, 'GET', null, normalize, false, isJson);
}

export function post(url, body, authRequest = false) {
  return _asyncRequest(url, 'POST', body, true, authRequest);
}

export function put(url, body) {
  return _asyncRequest(url, 'PUT', body);
}

export function del(url, body, authRequest = false) {
  return _asyncRequest(url, 'DELETE', body, true, authRequest);
}

export function uploadWithParams(url, key, file, extra) {
  const headersForRequest = getHeaders('POST');
  delete headersForRequest['Content-Type'];
  const formData = new FormData();
  formData.append(key, file);
  if (extra) {
    for (const index in extra) {
      if (isArray(extra[index])) {
        extra[index].forEach((item) => {
          formData.append(`${index}[]`, item);
        });
      } else {
        formData.append(index, extra[index]);
      }
    }
  }

  const result = {
    success: false,
    status: null,
    data: null,
  };

  return fetch(normalizeURL(url), {
    headers: headersForRequest,
    credentials: 'include',
    method: 'POST',
    body: formData,
  })
    .then((response) => {
      result.status = response.status;
      result.success = response.status < 400;
      return response.text();
    })
    .then((data) => {
      result.data = data;
      return result;
    })
    .catch((error) => {
      return false;
    });
}

export function upload(url, key, file) {
  const headersForRequest = getHeaders('POST');
  delete headersForRequest['Content-Type'];
  const formData = new FormData();
  formData.append(key, file);
  return fetch(normalizeURL(url), {
    headers: headersForRequest,
    credentials: 'include',
    method: 'POST',
    body: formData,
  })
    .then((response) => {
      return true;
    })
    .catch((error) => {
      return false;
    });
}

export const Api = {
  get,
  post,
  put,
  del,
  uploadWithParams,
  upload,
};
